import React from 'react';
import {Box, Button, ButtonBase, Collapse, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import {useTranslation} from 'react-i18next';
import {ImageValue} from '../../../models/Template';
import {Toggle} from '../../../scene/rowVisibility';
import {countFilled, FieldRow, FillField, isFieldFilled, rowSummary} from './fieldList';
import FieldCard from './FieldCard';
import SwitchCard from './SwitchCard';

interface GroupCardProps {
    row: FieldRow;
    /** The admin's name for the row (rowLabel); the row key when absent. */
    label?: string;
    selections: Record<string, string | ImageValue>;
    open: boolean;
    /** Header tap: open this row (closing any other) or close it. */
    onOpenToggle: (key: string) => void;
    /** This row's switches. */
    toggles: Toggle[];
    isOn: (toggle: Toggle) => boolean;
    onToggle: (toggle: Toggle, on: boolean) => void;
    onHide: (key: string) => void;
    /** This row is being moved on the canvas (row reposition). */
    repositioning?: boolean;
    /** Enter row reposition, or leave it when this row is the one moving. */
    onReposition?: (key: string) => void;
    onTextTap: (field: FillField) => void;
    onImageTap: (field: FillField) => void;
}

/** One repeated row (a game, a result) as a single collapsible card: its fields
 *  are filled together, and the whole row can leave the design. The fields keep
 *  the flat checklist's cards and sheets, numbered within the row — the same
 *  numbers the canvas shows while this row is open. */
const GroupCard: React.FC<GroupCardProps> = ({
                                                 row,
                                                 label = row.key,
                                                 selections,
                                                 open,
                                                 onOpenToggle,
                                                 toggles,
                                                 isOn,
                                                 onToggle,
                                                 onHide,
                                                 repositioning = false,
                                                 onReposition,
                                                 onTextTap,
                                                 onImageTap,
                                             }) => {
    const {t} = useTranslation();
    const filled = countFilled(row.fields, selections);
    const total = row.fields.length;
    const summary = rowSummary(row.fields, selections);
    return (
        <Box data-testid={`row-card-${row.key}`} sx={{
            border: `1px solid ${open ? '#2ea6e6' : '#ffffff50'}`,
            boxShadow: open ? '0 0 0 2px #2ea6e633' : 'none',
            borderRadius: '14px',
            mb: 1.5,
            backgroundColor: '#0a1f33',
            transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
        }}>
            {/* A div-based ButtonBase: focusable, Enter/Space open it, and it
                may hold the block-level title/summary a <button> can't. */}
            <ButtonBase
                component="div"
                disableRipple
                aria-expanded={open}
                data-testid={`row-header-${row.key}`}
                onClick={() => onOpenToggle(row.key)}
                sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'flex-start',
                    textAlign: 'inherit',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: '14px',
                    '&.Mui-focusVisible': {outline: '2px solid #2ea6e6', outlineOffset: -2},
                }}
            >
                <Box sx={{flexGrow: 1, minWidth: 0}}>
                    <Typography sx={{color: 'white', fontWeight: 600, fontSize: '0.95rem'}} noWrap>
                        {label}
                    </Typography>
                    <Typography dir="auto" noWrap sx={{color: summary ? '#C3F3FD' : '#7fa1ba', fontSize: '0.85rem'}}>
                        {summary || t('editor.rows.emptySummary')}
                    </Typography>
                </Box>
                <Typography sx={{
                    color: total > 0 && filled === total ? '#17c47f' : '#7fa1ba',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    flexShrink: 0,
                }}>
                    {filled}/{total}
                </Typography>
                <ExpandMoreIcon sx={{
                    color: '#C3F3FD',
                    transform: open ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.3s ease',
                }}/>
            </ButtonBase>
            <Collapse in={open} unmountOnExit>
                <Box sx={{px: 1.5, pb: 1.5}}>
                    {row.fields.map((field, index) => (
                        <FieldCard
                            key={field.key}
                            field={field}
                            number={index + 1}
                            value={selections[field.key]}
                            filled={isFieldFilled(field, selections[field.key])}
                            highlighted={false}
                            onTextTap={onTextTap}
                            onImageTap={onImageTap}
                            compact
                        />
                    ))}
                    {toggles.map((toggle) => (
                        <SwitchCard
                            key={toggle.key}
                            label={toggle.label}
                            on={isOn(toggle)}
                            onChange={(on) => onToggle(toggle, on)}
                            compact
                        />
                    ))}
                    {onReposition && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<OpenWithIcon/>}
                            onClick={() => onReposition(row.key)}
                            data-testid={`row-reposition-${row.key}`}
                            aria-pressed={repositioning}
                            sx={{
                                mt: 0.5,
                                mr: 1,
                                color: '#C3F3FD',
                                borderColor: '#ffffff60',
                                textTransform: 'none',
                                borderRadius: '10px',
                            }}
                        >
                            {t(repositioning ? 'editor.fillMode.repositionApprove' : 'editor.rows.reposition')}
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityOffOutlinedIcon/>}
                        onClick={() => onHide(row.key)}
                        data-testid={`row-hide-${row.key}`}
                        sx={{
                            mt: 0.5,
                            color: '#C3F3FD',
                            borderColor: '#ffffff60',
                            textTransform: 'none',
                            borderRadius: '10px',
                        }}
                    >
                        {t('editor.rows.hide')}
                    </Button>
                </Box>
            </Collapse>
        </Box>
    );
};

export default GroupCard;
